import { useMemo, useState } from 'react';
import type { GetProductsResItem2Dto } from '@travelity/api/generated';
import { Box, Stack } from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Tag } from '@travelity/ui/tag';
import { SquareCard } from '@travelity/ui/square-card';
import { OverlayWithReason } from '@components/overlay-with-reason';
import { Card } from '@travelity/ui/card';
import { useTranslation } from 'react-i18next';
import { OnboardingClasses } from '@/onboarding';

import { PackageCardActions } from './components/PackageCardActions';
import { PackageItemProducts } from './components/PackageItemProducts';
import { timeOffsetToMinutes } from '@/utils/timeOffsetToMinutes';
import { getMinPriceWithCurrency } from './helpers/getMinPriceWithCurrency';

interface PackageItemProps {
    product: GetProductsResItem2Dto;
    isSelected: boolean;
}

const PackageItem = ({ product, isSelected }: PackageItemProps) => {
    const { t } = useTranslation('package');
    const [isPackageExpanded, setIsPackageExpanded] = useState(false);
    const totalMinPrice = useMemo(
        () => getMinPriceWithCurrency(product.products),
        [product.products]
    );

    const startTimeMinutes = timeOffsetToMinutes(
        product.products[0].items[0].start_time
    );
    const lastProduct =
        product.products[product.products.length - 1].items[
            product.products[product.products.length - 1].items.length - 1
        ];
    const lastProductStartTime = timeOffsetToMinutes(lastProduct.start_time);
    const lastProductDuration = timeOffsetToMinutes(
        lastProduct.schedule!.items[0].duration.estimated
    );

    const durationInMinutes =
        startTimeMinutes + (lastProductStartTime + lastProductDuration);

    const days = product.products[product.products.length - 1].offset_days + 1;
    const duration = `${days}d ${Math.round(durationInMinutes / 60)}h`;

    return (
        <>
            <Card
                className={OnboardingClasses.PRODUCT_CARD}
                sx={{
                    height: { xs: 'auto', sm: '185px' },
                }}
                isSelected={isSelected}
                parentProps={{ alignItems: 'stretch' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-evenly"
                buttons={
                    <PackageCardActions
                        product={product}
                        isPackageExpanded={isPackageExpanded}
                        setIsPackageExpanded={setIsPackageExpanded}
                    />
                }
            >
                <Stack
                    sx={{
                        width: { xs: '100%', sm: '50%' },
                        filter: !product.active ? 'blur(2px)' : undefined,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            px: 2,
                            height: '46px',
                            py: '10px',
                            bgcolor: '#F4F6FA',
                            borderRadius: '12px 0px 0px 0px',
                        }}
                    >
                        <Box
                            component="span"
                            title={product.name}
                            sx={{
                                color: '#8F97AA',
                                pr: 1,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {product.name}
                        </Box>
                        <Tag label={t('Type')} values={[t(product.type)]} />
                    </Stack>
                    <Stack
                        sx={{
                            grow: 2,
                            pl: 2,
                            pr: 2 + (3 - 1),
                            py: '10px',
                            height: { xs: '99px', sm: '139px' },
                        }}
                        direction="row"
                        justifyContent="space-evenly"
                        gap={1}
                    >
                        <SquareCard
                            title={t('Duration')}
                            value={duration}
                            Icon={AccessTimeIcon}
                        />
                        <SquareCard
                            title={t('Min Price')}
                            value={`${totalMinPrice.minPrice} ${totalMinPrice.currency}`.trim()}
                            Icon={PaymentsOutlinedIcon}
                        />
                        <SquareCard
                            title={t('Included Products')}
                            value={`${product.products.length}`}
                            Icon={Inventory2OutlinedIcon}
                        />
                    </Stack>
                </Stack>
                <Stack
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        width: { xs: '100%', sm: '50%' },
                        borderLeft: { xs: 'none', sm: '2px solid #C9CEDC' },
                        position: 'relative',
                        filter: !product.active ? 'blur(2px)' : undefined,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={2}
                        sx={{
                            px: 2,
                            height: { xs: 'auto', sm: '46px' },
                            py: { xs: '4px', sm: '10px' },
                            bgcolor: { xs: 'none', sm: '#F4F6FA' },
                            borderRadius: '0px 12px 0px 0px',
                        }}
                    >
                        <Inventory2OutlinedIcon sx={{ color: '#8F97AA' }} />
                        <Box component="span" sx={{ color: '#8F97AA' }}>
                            {t('Products')}
                        </Box>
                    </Stack>
                    <Stack sx={{ maxHeight: '139px', overflow: 'hidden' }}>
                        {product.products.map(p => (
                            <Stack
                                key={p.day_index}
                                direction="row"
                                gap={1}
                                p={1}
                                sx={{ overflow: 'hidden' }}
                            >
                                <span>Day {p.offset_days + 1}.</span>
                                <Stack>
                                    {p.items.map(item => (
                                        <Box key={item.id}>{item.ref.name}</Box>
                                    ))}
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
                {!!product.deleted && (
                    <OverlayWithReason title={t('Deleted')} />
                )}
                {!product.deleted && !product.active && (
                    <OverlayWithReason title={t('Inactive')} />
                )}
            </Card>
            {isPackageExpanded && (
                <PackageItemProducts products={product.products} />
            )}
        </>
    );
};

export default PackageItem;
